/**
 * overlays.js — modal, menu, confirm dialog and toasts.
 *
 * DEFECTS IN v1 THAT THIS FIXES
 *  - Modals were rendered with `onHide={null}`, `keyboard={false}` and
 *    `backdrop="static"`, so Escape was disabled and the backdrop was inert:
 *    the only exit was a custom close button. There was no focus trap and no
 *    focus restoration.
 *  - Dropdown menus were `<div onClick>` with no role and no keyboard access.
 *  - The user menu in the header opened on CSS hover only, so it was
 *    unreachable by keyboard entirely.
 *  - `alert()` was called in production code paths, including
 *    `alert("Failed to create item. See console.")`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon, ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from './icons';
import { Button, cx } from './primitives';

/* ------------------------------------------------------------------ *
 * Focus trap
 * ------------------------------------------------------------------ */

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function useFocusTrap(active, containerRef, onEscape) {
  useEffect(() => {
    if (!active) return undefined;

    const previouslyFocused = document.activeElement;
    const container = containerRef.current;

    // Move focus into the dialog.
    const first = container?.querySelector(FOCUSABLE);
    (first || container)?.focus?.();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onEscape?.();
        return;
      }
      if (event.key !== 'Tab' || !container) return;

      const focusables = [...container.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null
      );
      if (focusables.length === 0) return;

      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = overflow;
      // Restore focus to whatever opened the dialog.
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [active, containerRef, onEscape]);
}

/* ------------------------------------------------------------------ *
 * Modal
 * ------------------------------------------------------------------ */

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  /** Set for destructive flows where an accidental Escape would lose work. */
  dismissible = true,
  initialFocusRef,
}) {
  const dialogRef = useRef(null);
  const titleId = useId();
  const descId = useId();

  const handleEscape = useCallback(() => {
    if (dismissible) onClose?.();
  }, [dismissible, onClose]);

  useFocusTrap(open, dialogRef, handleEscape);

  useEffect(() => {
    if (open && initialFocusRef?.current) initialFocusRef.current.focus();
  }, [open, initialFocusRef]);

  if (!open) return null;

  return createPortal(
    // The scrim is presentational: it exists only to catch a click outside the
    // dialog. Keyboard users dismiss with Escape, which the focus trap handles,
    // so this element deliberately has no keyboard handler of its own.
    <div
      className="modal-scrim"
      role="presentation"
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        className={cx('modal', size !== 'md' && `modal-${size}`)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
      >
        {(title || dismissible) && (
          <header className="modal-header">
            <div>
              {title && (
                <h2 className="modal-title" id={titleId}>
                  {title}
                </h2>
              )}
              {description && (
                <p className="modal-description" id={descId}>
                  {description}
                </p>
              )}
            </div>
            {/* Full size, not `sm`. This is the control a user reaches for
                when a dialog has trapped their focus, and it was a 14px glyph
                in a 32px box floating beside an 18px title. */}
            {dismissible && (
              <Button
                variant="ghost"
                iconOnly
                icon={CloseIcon}
                onClick={onClose}
                aria-label="Close dialog"
              />
            )}
          </header>
        )}
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </div>,
    document.body
  );
}

/* ------------------------------------------------------------------ *
 * Confirm dialog — replaces window.confirm and the ad-hoc delete modals
 * ------------------------------------------------------------------ */

export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title = 'Are you sure?',
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  pending = false,
  /** When set, the user must type this exact text to enable the action. */
  confirmPhrase = null,
  children,
}) {
  const [typed, setTyped] = useState('');
  const cancelRef = useRef(null);

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const canConfirm = !confirmPhrase || typed.trim() === confirmPhrase;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      description={description}
      size="sm"
      initialFocusRef={cancelRef}
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={pending}
            disabled={!canConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {confirmPhrase && (
        <div className="field mt-3">
          <label className="field-label" htmlFor="confirm-phrase">
            Type <strong>{confirmPhrase}</strong> to confirm
          </label>
          <input
            id="confirm-phrase"
            className="input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
          />
        </div>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------ *
 * Menu
 * ------------------------------------------------------------------ */

export function Menu({ trigger, children, align = 'end', label = 'Actions' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const items = [...(menuRef.current?.querySelectorAll('.menu-item:not(:disabled)') || [])];
      if (!items.length) return;
      const current = items.indexOf(document.activeElement);
      const next =
        e.key === 'ArrowDown'
          ? Math.min(current + 1, items.length - 1)
          : Math.max(current - 1, 0);
      items[current === -1 ? 0 : next].focus();
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
      {trigger({
        onClick: () => setOpen((o) => !o),
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': menuId,
      })}
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          className="menu"
          role="menu"
          aria-label={label}
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--space-1))',
            [align === 'end' ? 'right' : 'left']: 0,
            zIndex: 'var(--z-dropdown)',
          }}
        >
          {/* Items close the menu themselves through context, rather than the
              container catching a bubbled click — a click handler on a div
              with role="menu" is neither focusable nor keyboard-operable. */}
          <MenuCloseContext.Provider value={() => setOpen(false)}>
            {children}
          </MenuCloseContext.Provider>
        </div>
      )}
    </div>
  );
}

const MenuCloseContext = createContext(() => {});

export function MenuItem({ icon: Icon, danger = false, children, onClick, ...rest }) {
  const close = useContext(MenuCloseContext);
  return (
    <button
      type="button"
      role="menuitem"
      className={cx('menu-item', danger && 'menu-item-danger')}
      onClick={(event) => {
        close();
        onClick?.(event);
      }}
      {...rest}
    >
      {Icon && <Icon size={15} aria-hidden="true" />}
      {children}
    </button>
  );
}

export function MenuSeparator() {
  return <div className="menu-separator" role="separator" />;
}

export function MenuLabel({ children }) {
  return <div className="menu-label">{children}</div>;
}

/* ------------------------------------------------------------------ *
 * Toasts
 * ------------------------------------------------------------------ */

const ToastContext = createContext(null);

const TOAST_ICONS = {
  success: SuccessIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  info: InfoIcon,
};

let toastSeq = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (toast) => {
      toastSeq += 1;
      const id = `toast-${toastSeq}`;
      const duration = toast.duration ?? (toast.tone === 'error' ? 8000 : 4000);
      setToasts((list) => [...list, { ...toast, id }]);
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration)
        );
      }
      return id;
    },
    [dismiss]
  );

  // Clear every pending timer on unmount.
  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((t) => clearTimeout(t));
      map.clear();
    };
  }, []);

  const api = useMemo(
    () => ({
      push,
      dismiss,
      success: (message, opts = {}) => push({ tone: 'success', message, ...opts }),
      error: (message, opts = {}) => push({ tone: 'error', message, ...opts }),
      warning: (message, opts = {}) => push({ tone: 'warning', message, ...opts }),
      info: (message, opts = {}) => push({ tone: 'info', message, ...opts }),
    }),
    [push, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="toast-region" role="region" aria-label="Notifications">
          {toasts.map((toast) => {
            const Icon = TOAST_ICONS[toast.tone] || InfoIcon;
            return (
              <div
                key={toast.id}
                className={cx('toast', `toast-${toast.tone}`)}
                role={toast.tone === 'error' ? 'alert' : 'status'}
                aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
              >
                <Icon className="toast-icon" size={15} aria-hidden="true" />
                <div className="toast-body">
                  {toast.title && <div className="toast-title">{toast.title}</div>}
                  <div className={toast.title ? 'toast-message' : undefined}>{toast.message}</div>
                  {toast.action && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm mt-2"
                      onClick={() => {
                        toast.action.onClick();
                        dismiss(toast.id);
                      }}
                    >
                      {toast.action.label}
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm btn-icon-only"
                  aria-label="Dismiss notification"
                  onClick={() => dismiss(toast.id)}
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside a ToastProvider.');
  return context;
}
